import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import supabase, { supabaseAnonKey } from "../../../../utils/supabase";
import { useStatusDialog } from "../../../common/StatusDialogProvider";
import DialogScientificNameEditor from "./DialogScientificNameEditor";
import ScientificNameSelector from "./parts/ScientificNameSelector";
import GbifGroupedImportReview from "./GbifGroupedImportReview";

const REQUIRED_FIELDS = ["name_spell_valid", "current_rank", "gbif_id"];

const RANK_WEIGHT = {
  kingdom: 0,
  phylum: 1,
  class: 2,
  order: 3,
  family: 4,
  subfamily: 5,
  tribe: 6,
  subtribe: 7,
  genus: 8,
  subgenus: 9,
  species: 10,
  subspecies: 11,
};

const wait = (ms) => new Promise((res) => setTimeout(res, ms));

const createSelectionModel = (ids = [], type = "include") => ({
  type: type === "exclude" ? "exclude" : "include",
  ids: new Set((ids || []).map((x) => String(x))),
});

const selectionModelIds = (model) => Array.from(model?.ids || []).map((x) => String(x));

const isLikelyGbifId = (val) => !!val && /^\d+$/.test(String(val).trim());

const buildFunctionHeaders = () => {
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY || supabaseAnonKey || "";
  if (!key) return undefined;
  return { apikey: key, Authorization: `Bearer ${key}` };
};

function generateId(prefix = "sci_name") {
  const guid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  return `${prefix}_${guid}`;
}

function mapGbifTaxon(t, level = 0, parentCanonical = "", parentDbId = null) {
  const today = new Date().toISOString().slice(0, 10);
  const status = (t.status || t.taxonomicStatus || "").toString().toLowerCase();
  const rankRaw = (t.rank || "").toString().toLowerCase();
  const acceptedKey = t.acceptedKey || t.acceptedUsageKey || null;
  const acceptedName = t.acceptedScientificName || t.acceptedCanonicalName || "";
  const rank =
    status === "synonym"
      ? "synonym"
      : status === "homonym"
        ? "homonym"
        : status && status !== "accepted" && status !== "valid"
          ? status
          : rankRaw;
  const authorship = t.authorship || "";
  const yearMatch = authorship.match(/\b(1[6-9]\d{2}|20\d{2})\b/);
  const remarkParts = [`Initial data automatically added via GBIF API in ${today}.`, `GBIF key: ${t.key}`];
  if (authorship) remarkParts.push(`authorship: ${authorship}`);

  const deriveEpithet = () => {
    const name = t.canonicalName || t.scientificName || "";
    if (parentCanonical && name.toLowerCase().startsWith(parentCanonical.toLowerCase() + " ")) {
      const rest = name.slice(parentCanonical.length).trim();
      const parts = rest.split(/\s+/).filter(Boolean);
      return parts[parts.length - 1] || name;
    }
    const parts = name.split(/\s+/).filter(Boolean);
    return parts[parts.length - 1] || name;
  };

  const parentToken =
    status !== "accepted" && status !== "valid" && acceptedKey
      ? String(acceptedKey)
      : t.parentKey
        ? String(t.parentKey)
        : parentDbId || null;
  const gbifId = t.key ? String(t.key) : generateId("gbif");
  const originalSpelling = (() => {
    const base = t.scientificName || t.canonicalName || "";
    const auth = authorship ? ` | authorship: ${authorship}` : "";
    const specific = t.specificEpithet ? ` | specificEpithet: ${t.specificEpithet}` : "";
    return `${base}${auth}${specific}`;
  })();

  return {
    id: gbifId,
    gbif_id: gbifId,
    gbif_status: t.status || "",
    gbif_accepted_key: t.acceptedKey ? String(t.acceptedKey) : null,
    name_spell_valid: deriveEpithet(),
    name_spell_original: originalSpelling,
    current_rank: rank,
    current_parent: parentToken,
    authority_from_gbif: authorship || "",
    original_rank: null,
    original_parent: null,
    valid_name_id: null,
    type_taxa_id: null,
    type_category: null,
    type_locality: null,
    type_sex: null,
    type_repository_id: null,
    type_host: null,
    source_of_original_description: null,
    page: null,
    extant_fossil: null,
    last_update: new Date().toISOString(),
    authority_year: yearMatch ? yearMatch[1] : "",
    remark: remarkParts.join(" "),
    level,
    _gbif_accepted: acceptedKey ? String(acceptedKey) : null,
    _gbif_accepted_name: acceptedName || null,
  };
}

export default function DialogGbifBulkImport({ open, onClose }) {
  const { showStatus } = useStatusDialog() || {};

  const [parentDbId, setParentDbId] = useState(null);
  const [parentGbifId, setParentGbifId] = useState("");
  const [parentName, setParentName] = useState("");
  const [parentAddOpen, setParentAddOpen] = useState(false);
  const [showBold, setShowBold] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [editDialogRow, setEditDialogRow] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState(() => createSelectionModel());
  const [gbifSearchOpen, setGbifSearchOpen] = useState(false);
  const [gbifSearchLoading, setGbifSearchLoading] = useState(false);
  const [gbifSearchResults, setGbifSearchResults] = useState([]);
  const [gbifSearchError, setGbifSearchError] = useState("");
  const [gbifSearchQuery, setGbifSearchQuery] = useState("");
  const [selectedGbifKey, setSelectedGbifKey] = useState("");
  const [lastFetchedGbifKey, setLastFetchedGbifKey] = useState("");
  const [progressFetched, setProgressFetched] = useState(0);
  const [progressTotal, setProgressTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [conflicts, setConflicts] = useState([]);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictSelections, setConflictSelections] = useState({});
  const [pendingPayload, setPendingPayload] = useState(null);
  const [pendingSelectedRows, setPendingSelectedRows] = useState(null);
  const [groupedReviewOpen, setGroupedReviewOpen] = useState(false);

  const normalizeSelectionModel = useCallback((m) => {
    if (m && typeof m === "object") {
      const type = m.type === "exclude" ? "exclude" : "include";
      if (m.ids instanceof Set) return createSelectionModel(Array.from(m.ids), type);
      if (Array.isArray(m.ids)) return createSelectionModel(m.ids, type);
      if (Array.isArray(m.selectionModel)) return createSelectionModel(m.selectionModel, type);
      if (m.selectionModel instanceof Set) return createSelectionModel(Array.from(m.selectionModel), type);
    }
    if (Array.isArray(m)) return createSelectionModel(m);
    return createSelectionModel();
  }, []);

  const openGbifSearchDialog = useCallback(
    async (queryOverride) => {
      const query = (queryOverride || parentName || "").trim();
      if (!query || query.length < 3) {
        setGbifSearchError("親の学名が取得できていません。");
        setGbifSearchOpen(true);
        return null;
      }
      setGbifSearchOpen(true);
      setGbifSearchLoading(true);
      setGbifSearchError("");
      setGbifSearchResults([]);
      setGbifSearchQuery(query);
      setSelectedGbifKey("");
      const headers = buildFunctionHeaders();
      const { data, error: err } = await supabase.functions.invoke("gbif-search", {
        headers,
        body: { q: query, limit: 20 },
      });
      setGbifSearchLoading(false);
      if (err) {
        setGbifSearchError(err.message || "親のGBIF検索に失敗しました。");
        return null;
      }
      let resolved = data;
      if (typeof data === "string") {
        try {
          resolved = JSON.parse(data);
        } catch (_e) {
          setGbifSearchError("親のGBIF検索応答をJSONとして解析できませんでした。");
          return null;
        }
      }
      if (!Array.isArray(resolved) || resolved.length === 0) {
        setGbifSearchError("GBIF検索で親の候補が見つかりませんでした。");
        return null;
      }
      setGbifSearchResults(resolved);
      const firstKey = resolved[0]?.key ? String(resolved[0].key) : "";
      if (firstKey) setSelectedGbifKey(firstKey);
      return firstKey || null;
    },
    [parentName]
  );

  const handleParentChange = useCallback(
    async (id) => {
      setParentDbId(id || null);
      setParentGbifId("");
      setParentName("");
      if (!id) return;

      const { data, error: err } = await supabase
        .from("scientific_names")
        .select("id, name_spell_valid, name_spell_original, gbif_id")
        .eq("id", id)
        .maybeSingle();
      if (err) {
        console.error("[GBIF bulk] parent load error", err);
        setError(err.message || "親の取得に失敗しました。");
        return;
      }
      const pname = data?.name_spell_valid || data?.name_spell_original || "";
      setParentName(pname);
      const gbifIdVal = data?.gbif_id;
      if (isLikelyGbifId(gbifIdVal)) {
        setParentGbifId(String(gbifIdVal));
        showStatus?.({
          severity: "info",
          title: "親のGBIF IDを適用",
          description: `GBIF ID: ${gbifIdVal}`,
        });
      } else {
        await openGbifSearchDialog(pname);
      }
    },
    [showStatus, openGbifSearchDialog]
  );

  const searchParentGbifId = useCallback(async () => {
    if (!parentDbId) {
      setError("親を選択してください。");
      return;
    }
    await openGbifSearchDialog(parentName);
  }, [parentDbId, parentName, openGbifSearchDialog]);

  const fetchTree = useCallback(
    async (gbifKey) => {
      if (!gbifKey) {
        setError("親のGBIF IDが設定されていません。検索してから配下を取得してください。");
        return;
      }
      const parentGbifKeyStr = String(gbifKey);
      setLastFetchedGbifKey(parentGbifKeyStr);
      setError("");
      setSuccess("");
      setLoading(true);
      setRows([]);
      setSelectionModel(createSelectionModel());
      setProgressFetched(0);
      setProgressTotal(null);
      let allMapped = [];
      let cursor = null;
      let safety = 0;
      let lastErr = null;
      const headers = buildFunctionHeaders();
      console.debug("[GBIF bulk] tree fetch start", { key: gbifKey });
      const invokeTreeFetch = async (cursorToken) => {
        const maxRetry = 3;
        for (let attempt = 1; attempt <= maxRetry; attempt += 1) {
          const { data, error: err } = await supabase.functions.invoke("gbif-tree-fetch", {
            headers,
            body: {
              key: gbifKey,
              depth: 5,
              limitPerLevel: 500,
              cursor: cursorToken,
              includeSynonyms: true,
            },
            parseResponse: true,
          });
          if (!err) return { data };
          if (attempt === maxRetry) return { error: err };
          await wait(200 * attempt);
        }
        return { error: new Error("unknown invoke error") };
      };
      while (safety < 400) {
        safety += 1;
        const { data, error: err } = await invokeTreeFetch(cursor);
        if (err) {
          lastErr = err;
          break;
        }
        let resolved = data;
        if (typeof data === "string") {
          try {
            resolved = JSON.parse(data);
          } catch (e) {
            console.error("[GBIF bulk] tree fetch parse error", e, data);
            lastErr = e;
            break;
          }
        }
        if (!resolved) {
          setError("GBIF配下取得の応答が空です。");
          break;
        }

        const descendantList = Array.isArray(resolved.descendants)
          ? resolved.descendants
          : Array.isArray(resolved.items)
            ? resolved.items
            : [];

      console.debug("[GBIF bulk] raw tree data", {
        root: resolved.root,
        descendantsSample: descendantList.slice(0, 5),
        descendantCount: descendantList.length,
        cursor,
        nextCursor: resolved?.nextCursor || resolved?.cursor || null,
        acceptedSample: descendantList
          .filter((d) => d.acceptedKey || d.acceptedUsageKey)
          .slice(0, 3)
          .map((d) => ({ key: d.key, acceptedKey: d.acceptedKey || d.acceptedUsageKey, status: d.status })),
        raw: resolved,
      });

        if (typeof resolved.total === "number") setProgressTotal(resolved.total);
        if (typeof resolved.fetched === "number") {
          setProgressFetched(resolved.fetched);
        } else {
          setProgressFetched((prev) => prev + descendantList.length);
        }

        if (resolved.root && cursor === null) {
          // rootは初回のみ追加
          allMapped.push(mapGbifTaxon(resolved.root, 0, parentName, parentDbId));
        }
        descendantList.forEach((d) => allMapped.push(mapGbifTaxon(d, d.level || 1, parentName, parentDbId)));

        cursor = resolved?.nextCursor || resolved?.cursor || null;
        if (!cursor) break;
        await wait(150); // サーバー負荷軽減
      }

      setLoading(false);
      setProgressFetched((prev) => (allMapped.length ? allMapped.length : prev));
      if (lastErr) {
        setError(lastErr.message || "GBIF tree fetch failed");
        showStatus?.({
          severity: "error",
          title: "GBIF配下取得エラー",
          description: lastErr.message || "GBIF tree fetch failed",
        });
        return;
      }
      if (!allMapped.length) {
        setError("GBIF配下取得で有効なデータが得られませんでした。");
        return;
      }

      const gbifParentToken = parentGbifId ? String(parentGbifId) : parentGbifKeyStr;
      if (parentDbId && gbifParentToken) {
        allMapped.forEach((row) => {
          if (row.current_parent === gbifParentToken) {
            row.current_parent = parentDbId;
          }
        });
      }

      const filtered = allMapped.filter(
        (r) => String(r.id) !== parentGbifKeyStr && String(r.gbif_id || "") !== parentGbifKeyStr
      );
      const filteredBold = showBold
        ? filtered
        : filtered.filter((r) => !String(r.name_spell_valid || "").toLowerCase().includes("bold"));

      let filteredDup = filteredBold;
      if (!showDuplicates) {
        const gbifIds = filteredBold.map((r) => r.gbif_id).filter(Boolean);
        if (gbifIds.length) {
          const { data: existing, error: dupErr } = await supabase
            .from("scientific_names")
            .select("gbif_id")
            .in("gbif_id", gbifIds);
          if (dupErr) {
            console.error("[GBIF bulk] duplicate check error", dupErr);
          } else if (existing) {
            const existingSet = new Set(existing.filter((e) => e?.gbif_id).map((e) => String(e.gbif_id)));
            filteredDup = filteredBold.filter((r) => !existingSet.has(String(r.gbif_id)));
          }
        }
      }

      setRows(filteredDup);
      setSelectionModel(createSelectionModel(filteredDup.map((r) => r.id)));
      if (allMapped.length > 1000) {
        showStatus?.({
          severity: "warning",
          title: "大量データを取得しました",
          description: `取得件数が多いです(${allMapped.length}件)。必要な行だけ選択して登録してください。`,
        });
      }
    },
    [parentDbId, showStatus, parentGbifId, parentName, showBold, showDuplicates]
  );

  const applyGbifSelection = useCallback(
    async (gbifKey, candidate, options = {}) => {
      if (!gbifKey) return;
      const keyStr = String(gbifKey);
      setError("");
      setSuccess("");
      setParentGbifId(keyStr);
      setGbifSearchOpen(false);
      setGbifSearchError("");
      if (candidate?.scientificName || candidate?.canonicalName) {
        setParentName((prev) => prev || candidate.scientificName || candidate.canonicalName || "");
      }
      if (options.fetchDescendants) {
        await fetchTree(keyStr);
      }
    },
    [fetchTree]
  );

  const handleApplyGbifSelection = useCallback(
    async (withDescendants = false) => {
      if (!selectedGbifKey) return;
      const candidate = gbifSearchResults.find((r) => String(r?.key) === String(selectedGbifKey));
      await applyGbifSelection(selectedGbifKey, candidate, { fetchDescendants: withDescendants });
    },
    [applyGbifSelection, gbifSearchResults, selectedGbifKey]
  );

  const columns = useMemo(
    () => [
      {
        field: "__actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button size="small" variant="outlined" onClick={() => setEditDialogRow(params.row)}>
            編集
          </Button>
        ),
      },
      { field: "id", headerName: "ID", width: 200 },
      {
        field: "current_parent",
        headerName: "Parent",
        width: 200,
        editable: true,
        renderCell: (p) => {
          const val = p?.row?._gbif_accepted
            ? String(p.row._gbif_accepted)
            : p?.row?.current_parent
              ? String(p.row.current_parent)
              : "";
          if (!val) return <Typography variant="body2" color="text.secondary">未設定</Typography>;
          const displayVal = val;
          return <Typography variant="body2">{displayVal}</Typography>;
        },
      },
      { field: "current_rank", headerName: "Rank", width: 120, editable: true },
      { field: "name_spell_valid", headerName: "Name (valid)", flex: 1, minWidth: 200, editable: true },
      {
        field: "authority_from_gbif",
        headerName: "Authority (GBIF)",
        flex: 1,
        minWidth: 200,
        editable: false,
        renderCell: (p) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {p?.row?.authority_from_gbif || ""}
          </Typography>
        ),
      },
      { field: "authority_year", headerName: "Year", width: 90, editable: true },
      { field: "name_spell_original", headerName: "Original", flex: 1, minWidth: 200, editable: true },
      { field: "gbif_id", headerName: "GBIF TaxonID", width: 140 },
      {
        field: "remark",
        headerName: "Remark",
        flex: 1,
        minWidth: 220,
        editable: true,
        renderCell: (p) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {p?.row?.remark || ""}
          </Typography>
        ),
      },
      {
        field: "gbif_status",
        headerName: "GBIF Status",
        width: 140,
        editable: false,
        renderCell: (p) => (
          <Chip size="small" label={p?.row?.gbif_status || "-"} variant="outlined" />
        ),
      },
    ],
    [parentDbId, parentGbifId, lastFetchedGbifKey]
  );

  const LoadingOverlay = () => {
    const hasTotal = typeof progressTotal === "number" && progressTotal > 0;
    const value = hasTotal ? Math.min(100, Math.round((progressFetched / progressTotal) * 100)) : undefined;
    return (
      <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: "100%", py: 4 }}>
        <CircularProgress size={36} variant={value !== undefined ? "determinate" : "indeterminate"} value={value} />
        <Typography variant="body2">Loading children from GBIF...</Typography>
        <Typography variant="caption" color="text.secondary">
          取得済み: {progressFetched.toLocaleString()}
          {hasTotal ? ` / 約 ${progressTotal.toLocaleString()}` : ""}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          グループの規模によっては時間がかかることがあります。
        </Typography>
      </Stack>
    );
  };

  const getCellClassName = useCallback((params) => {
    if (REQUIRED_FIELDS.includes(params.field)) {
      const v = params.value;
      if (v === null || v === undefined || `${v}`.trim() === "") return "gbif-missing";
    }
    return "";
  }, []);

  const processRowUpdate = useCallback((updatedRow) => {
    setRows((prev) => prev.map((r) => (r.id === updatedRow.id ? updatedRow : r)));
    return updatedRow;
  }, []);

  const handleRegister = useCallback(async () => {
    setError("");
    setSuccess("");

    const idsArray = selectionModelIds(selectionModel);
    const selectedIdSet = new Set(idsArray);
    const selectedRows =
      selectionModel?.type === "exclude"
        ? rows.filter((r) => !selectedIdSet.has(String(r.id)))
        : rows.filter((r) => selectedIdSet.has(String(r.id)));
    const gbifParentToken = parentGbifId || lastFetchedGbifKey;
    const normalizedSelectedRows = selectedRows.map((r) => {
      const acceptedParent = r._gbif_accepted ? String(r._gbif_accepted) : null;
      const needsParentReplace =
        parentDbId && gbifParentToken && String(r.current_parent) === String(gbifParentToken);
      if (needsParentReplace) {
        return { ...r, current_parent: parentDbId };
      }
      if (acceptedParent) {
        return { ...r, current_parent: acceptedParent };
      }
      return r;
    });
    console.debug("[GBIF bulk] selection debug", {
      selectionModel,
      selectionIds: Array.from(selectedIdSet),
      selectionType: selectionModel?.type,
      totalRows: rows.length,
      rowIdSample: rows.slice(0, 10).map((r) => String(r.id)),
      selectedCount: normalizedSelectedRows.length,
      selectedIds: normalizedSelectedRows.map((r) => r.id),
      selectedParentsSample: normalizedSelectedRows.slice(0, 8).map((r) => ({
        id: r.id,
        gbif_id: r.gbif_id,
        parent: r.current_parent,
        accepted: r._gbif_accepted,
      })),
    });
    if (normalizedSelectedRows.length === 0) {
      setError("登録対象を選択してください。");
      return;
    }

    const missing = normalizedSelectedRows.filter((r) =>
      REQUIRED_FIELDS.some((f) => !r[f] || `${r[f]}`.trim() === "")
    );
    if (missing.length > 0) {
      setError("必須項目が空の行があります。赤色セルを埋めてください。");
      return;
    }

    const gbifIds = normalizedSelectedRows.map((r) => r.gbif_id).filter(Boolean);
    let existingRows = [];
    if (gbifIds.length) {
      const { data: exists, error: exErr } = await supabase
        .from("scientific_names")
        .select("id,gbif_id,current_parent,current_rank,name_spell_valid,authority_year")
        .in("gbif_id", gbifIds);
      if (exErr) {
        setError(exErr.message || "重複チェックに失敗しました。");
        return;
      }
      existingRows = exists || [];
    }

    const parentsForCheck = Array.from(new Set(normalizedSelectedRows.map((r) => r.current_parent).filter(Boolean)));
    const existingComboSet = new Set();
    const existingRankComboSet = new Set();
    if (parentsForCheck.length) {
      const { data: comboRows, error: comboErr } = await supabase
        .from("scientific_names")
        .select("current_parent,name_spell_valid,authority_year,current_rank")
        .in("current_parent", parentsForCheck);
      if (comboErr) {
        console.error("[GBIF bulk] combo duplicate check error", comboErr);
      } else {
        (comboRows || []).forEach((row) => {
          const keyYear = `${row.current_parent || ""}|||${(row.name_spell_valid || "").trim()}|||${row.authority_year || ""}`;
          const keyRank = `${row.current_parent || ""}|||${(row.name_spell_valid || "").trim()}|||${(row.current_rank || "").trim()}`;
          existingComboSet.add(keyYear);
          existingRankComboSet.add(keyRank);
        });
      }
    }

    const localComboSet = new Set();
    const localRankComboSet = new Set();

    const sortedRows = [...normalizedSelectedRows].sort((a, b) => {
      const wa = RANK_WEIGHT[a.current_rank] ?? 100;
      const wb = RANK_WEIGHT[b.current_rank] ?? 100;
      return wa - wb;
    });

    const payloadCandidates = sortedRows
      .filter((r) => {
        const keyYear = `${r.current_parent || ""}|||${(r.name_spell_valid || "").trim()}|||${r.authority_year || ""}`;
        const keyRank = `${r.current_parent || ""}|||${(r.name_spell_valid || "").trim()}|||${(r.current_rank || "").trim()}`;
        if (existingComboSet.has(keyYear) || localComboSet.has(keyYear)) return false;
        if (existingRankComboSet.has(keyRank) || localRankComboSet.has(keyRank)) return false;
        localComboSet.add(keyYear);
        localRankComboSet.add(keyRank);
        return true;
      })
      .map((r) => ({
        id: r.gbif_id || r.id || generateId("sci_name"),
        gbif_id: r.gbif_id || r.id || null,
        name_spell_valid: r.name_spell_valid,
        name_spell_original: r.name_spell_original || r.name_spell_valid,
        current_rank: r.current_rank,
        current_parent: r.current_parent || null,
        authority_from_gbif: r.authority_from_gbif || null,
        original_rank: r.original_rank || null,
        original_parent: r.original_parent || null,
        valid_name_id: r.valid_name_id || null,
        type_taxa_id: r.type_taxa_id || null,
        type_category: r.type_category || null,
        type_locality: r.type_locality || null,
        type_sex: r.type_sex || null,
        type_repository_id: r.type_repository_id || null,
        extant_fossil: r.extant_fossil || null,
        authority_year: r.authority_year || null,
        remark: r.remark || null,
        last_update: new Date().toISOString(),
      }));

    const existingMap = new Map((existingRows || []).map((e) => [String(e.gbif_id), e]));
    const conflictsFound = [];
    const filteredPayload = [];
    for (const row of payloadCandidates) {
      const existing = row.gbif_id ? existingMap.get(String(row.gbif_id)) : null;
      if (!existing) {
        filteredPayload.push(row);
        continue;
      }
      const parentDiff = String(existing.current_parent || "") !== String(row.current_parent || "");
      const rankDiff = String(existing.current_rank || "").toLowerCase() !== String(row.current_rank || "").toLowerCase();
      if (parentDiff || rankDiff) {
        conflictsFound.push({ incoming: row, existing });
      } else {
        // same as existing; skip
      }
    }

    if (conflictsFound.length > 0) {
      setConflicts(conflictsFound);
      setConflictSelections(
        conflictsFound.reduce((acc, c) => {
          acc[String(c.incoming.id)] = "new";
          return acc;
        }, {})
      );
      setPendingPayload(filteredPayload);
      setPendingSelectedRows(selectedRows);
      setConflictDialogOpen(true);
      return;
    }

    await processPayload(filteredPayload, normalizedSelectedRows);
  }, [rows, selectionModel, showStatus, onClose, selectionModelIds]);

  const processPayload = useCallback(
    async (payload, selectedRows) => {
      const parentsForCheck = Array.from(new Set(selectedRows.map((r) => r.current_parent).filter(Boolean)));
      const existingComboSet = new Set();
      const existingRankComboSet = new Set();
      if (parentsForCheck.length) {
        const { data: comboRows, error: comboErr } = await supabase
          .from("scientific_names")
          .select("current_parent,name_spell_valid,authority_year,current_rank")
          .in("current_parent", parentsForCheck);
        if (comboErr) {
          console.error("[GBIF bulk] combo duplicate check error", comboErr);
        } else {
          (comboRows || []).forEach((row) => {
            const keyYear = `${row.current_parent || ""}|||${(row.name_spell_valid || "").trim()}|||${row.authority_year || ""}`;
            const keyRank = `${row.current_parent || ""}|||${(row.name_spell_valid || "").trim()}|||${(row.current_rank || "").trim()}`;
            existingComboSet.add(keyYear);
            existingRankComboSet.add(keyRank);
          });
        }
      }

      const localComboSet = new Set();
      const localRankComboSet = new Set();

      const filteredPayload = payload.filter((r) => {
        const keyYear = `${r.current_parent || ""}|||${(r.name_spell_valid || "").trim()}|||${r.authority_year || ""}`;
        const keyRank = `${r.current_parent || ""}|||${(r.name_spell_valid || "").trim()}|||${(r.current_rank || "").trim()}`;
        if (existingComboSet.has(keyYear) || localComboSet.has(keyYear)) return false;
        if (existingRankComboSet.has(keyRank) || localRankComboSet.has(keyRank)) return false;
        localComboSet.add(keyYear);
        localRankComboSet.add(keyRank);
        return true;
      });

      const payloadIdSet = new Set(filteredPayload.map((p) => String(p.id)));
      const parentCandidates = Array.from(
        new Set(
          filteredPayload
            .map((r) => r.current_parent)
            .filter(Boolean)
            .map((pid) => String(pid))
        )
      );

      const parentCheckTargets = parentCandidates.filter((pid) => !payloadIdSet.has(pid));
      const existingParentSet = new Set();
      if (parentCheckTargets.length) {
        const { data: existParents, error: parentErr } = await supabase
          .from("scientific_names")
          .select("id")
          .in("id", parentCheckTargets);
        if (parentErr) {
          console.error("[GBIF bulk] parent existence check error", parentErr);
        } else {
          (existParents || []).forEach((row) => row?.id && existingParentSet.add(String(row.id)));
        }
      }

      const missingParents = parentCheckTargets.filter((pid) => !existingParentSet.has(pid));
      console.debug("[GBIF bulk] parent check debug", {
        parentCandidates,
        parentCheckTargets,
        existingParents: Array.from(existingParentSet),
        missingParents,
      });
      let effectivePayload = filteredPayload;
      if (missingParents.length) {
        const missingSet = new Set(missingParents);
        const excludedRows = filteredPayload.filter((r) => missingSet.has(String(r.current_parent)));
        effectivePayload = filteredPayload.filter((r) => !missingSet.has(String(r.current_parent)));

        if (excludedRows.length) {
          const msg = `親がDBに存在しないため ${excludedRows.length} 件を除外しました。`;
          setError(msg);
          const samples = excludedRows.slice(0, 5).map((r) => `${r.name_spell_valid} (parent: ${r.current_parent})`);
          showStatus?.({
            severity: "warning",
            title: "親が存在せず除外",
            description: `${msg} 例: ${samples.join(", ")}`,
          });
          console.debug("[GBIF bulk] excluded for missing parent", {
            excluded: excludedRows.map((r) => ({
              id: r.id,
              gbif_id: r.gbif_id,
              parent: r.current_parent,
              accepted: r._gbif_accepted,
            })),
          });
          const removedIds = excludedRows.map((r) => String(r.id));
          const updateSelectionForRemoved = (removed) => {
            if (!removed.length) return selectionModel;
            const removedSet = new Set(removed.map((x) => String(x)));
            if (selectionModel?.type === "exclude") {
              const nextIds = new Set(selectionModelIds(selectionModel));
              removedSet.forEach((id) => nextIds.add(id));
              return createSelectionModel(Array.from(nextIds), "exclude");
            }
            const nextIds = selectionModelIds(selectionModel).filter((id) => !removedSet.has(id));
            return createSelectionModel(nextIds, "include");
          };
          setSelectionModel(updateSelectionForRemoved(removedIds));
        }
      }

      if (effectivePayload.length === 0) {
        const msg = `重複や親未存在のため新規登録はありませんでした (選択: ${selectedRows.length})。`;
        setSuccess(msg);
        showStatus?.({ severity: "info", title: "登録スキップ", description: msg });
        return;
      }

      // 上位階級から順に登録
      const byRank = effectivePayload.reduce((acc, row) => {
        const key = row.current_rank || "unknown";
        acc[key] = acc[key] || [];
        acc[key].push(row);
        return acc;
      }, {});

      console.debug("[GBIF bulk] payload preview", {
        count: effectivePayload.length,
        sample: effectivePayload.slice(0, 10).map((r) => ({
          id: r.id,
          gbif_id: r.gbif_id,
          parent: r.current_parent,
          accepted: r._gbif_accepted,
        })),
      });

      const successIds = new Set();
      let failedRows = [];

      for (const rank of Object.keys(byRank).sort((a, b) => {
        const wa = RANK_WEIGHT[a] ?? 100;
        const wb = RANK_WEIGHT[b] ?? 100;
        return wa - wb;
      })) {
        const rowsForRank = byRank[rank];
        console.debug("[GBIF bulk] upsert rank batch", { rank, count: rowsForRank.length });
        const { error: insertErr } = await supabase
          .from("scientific_names")
          .upsert(rowsForRank, { onConflict: "gbif_id" });
        if (insertErr) {
          console.error("[GBIF bulk] upsert error, fallback per-row", insertErr);
          for (const row of rowsForRank) {
            const { error: rowErr } = await supabase
              .from("scientific_names")
              .upsert(row, { onConflict: "gbif_id" });
            if (rowErr) {
              failedRows.push(row);
              console.error("[GBIF bulk] row upsert failed", { id: row.id, parent: row.current_parent, err: rowErr });
            } else {
              successIds.add(String(row.id));
              await wait(50);
            }
          }
        } else {
          rowsForRank.forEach((r) => successIds.add(String(r.id)));
          await wait(300); // 軽めの反映待ち
        }
      }

      const newRows = rows.filter((r) => !successIds.has(String(r.id)));
      if (failedRows.length) {
        const failedIds = new Set(failedRows.map((r) => String(r.id)));
        const merged = [
          ...failedRows,
          ...newRows.filter((r) => !failedIds.has(String(r.id))),
        ];
        setRows(merged);
        setSelectionModel(createSelectionModel(failedRows.map((r) => r.id)));
        const failMsg = `一部で登録に失敗しました (${failedRows.length}件)。FK等を確認し修正してください。`;
        setError(failMsg);
        showStatus?.({
          severity: "warning",
          title: "一部登録失敗",
          description: `${failMsg} 例: ${failedRows.slice(0, 5).map((r) => `${r.name_spell_valid} (${r.current_parent})`).join(", ")}`,
        });
      } else {
        setRows(newRows);
        setSelectionModel(createSelectionModel());
        const msg = `登録完了: ${successIds.size}件`;
        setSuccess(msg);
        showStatus?.({ severity: "success", title: "GBIF一括登録成功", description: msg });
        onClose?.(true);
      }
    },
    [rows, selectionModel, showStatus, onClose, selectionModelIds]
  );

  return (
    <>
      <Dialog open={open} onClose={() => onClose?.(false)} maxWidth="lg" fullWidth>
        <DialogTitle>GBIFから一括登録</DialogTitle>
        <DialogContent sx={{ minHeight: 480 }}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              親となる学名（scientific_names）を選択し、GBIF の配下タクソンを取得して一括登録します。
            </Typography>

            <Stack spacing={1}>
              <Typography variant="subtitle2">親の学名（DBから選択）</Typography>
              <ScientificNameSelector
                valueId={parentDbId || ""}
                onChange={(id) => handleParentChange(id)}
                inlineAdd
                label="Parent Scientific Name"
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  現在のGBIF ID: {parentGbifId || "未設定"}
                </Typography>
                <Button variant="outlined" size="small" onClick={searchParentGbifId} disabled={!parentDbId}>
                  親のGBIF IDを検索
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!parentGbifId || !parentDbId || loading}
                  onClick={() => fetchTree(parentGbifId)}
                >
                  配下を取得
                </Button>
                <Button variant="text" size="small" onClick={() => setParentAddOpen(true)}>
                  親を新規追加
                </Button>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel id="bold-filter-label">BOLD</InputLabel>
                  <Select
                    labelId="bold-filter-label"
                    value={showBold ? "show" : "hide"}
                    label="BOLD"
                    onChange={(e) => setShowBold(e.target.value === "show")}
                  >
                    <MenuItem value="hide">BOLD除外</MenuItem>
                    <MenuItem value="show">BOLD表示</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="dup-filter-label">既存重複</InputLabel>
                  <Select
                    labelId="dup-filter-label"
                    value={showDuplicates ? "show" : "hide"}
                    label="既存重複"
                    onChange={(e) => setShowDuplicates(e.target.value === "show")}
                  >
                    <MenuItem value="hide">既存を除外</MenuItem>
                    <MenuItem value="show">既存も表示</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!parentName && !parentGbifId}
                  onClick={() => setGroupedReviewOpen(true)}
                >
                  グループ化レビュー
                </Button>
              </Stack>
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <Box sx={{ height: 360, width: "100%" }}>
              <DataGrid
                density="compact"
                rows={rows}
                columns={columns}
                checkboxSelection
                disableRowSelectionOnClick
                getRowId={(row) => String(row.id)}
                rowSelectionModel={selectionModelIds(selectionModel)}
                slots={{ loadingOverlay: LoadingOverlay }}
                onRowSelectionModelChange={(m) => {
                  const next = normalizeSelectionModel(m);
                  console.debug("[GBIF bulk] selection change", {
                    incoming: m,
                    normalizedType: next.type,
                    normalizedIds: selectionModelIds(next),
                  });
                  setSelectionModel(next);
                }}
                onRowDoubleClick={(params) => setEditDialogRow(params.row)}
                processRowUpdate={processRowUpdate}
                loading={loading}
                getCellClassName={getCellClassName}
                initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
                pageSizeOptions={[25, 50, 100]}
                sx={{ "& .gbif-missing": { backgroundColor: "#ffebee" } }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose?.(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleRegister} disabled={loading || rows.length === 0}>
            選択を登録
          </Button>
        </DialogActions>
      </Dialog>
      {groupedReviewOpen && (
        <GbifGroupedImportReview
          open
          q={parentName || gbifSearchQuery || ""}
          onClose={() => setGroupedReviewOpen(false)}
        />
      )}

      {parentAddOpen && <DialogScientificNameEditor open mode="add" onClose={() => setParentAddOpen(false)} />}

      {editDialogRow && (
        <DialogScientificNameEditor
          open
          mode="add"
          localMode
          scientificName={editDialogRow}
          onSave={(payload) => {
            setRows((prev) => prev.map((r) => (r.id === editDialogRow.id ? { ...r, ...payload } : r)));
          }}
          onClose={() => setEditDialogRow(null)}
        />
      )}

      {gbifSearchOpen && (
        <Dialog open={gbifSearchOpen} onClose={() => setGbifSearchOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>親のGBIF IDを検索</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                親: {parentName || "未選択"} {parentDbId ? `(ID: ${parentDbId})` : ""}
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ sm: "center" }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="学名で検索"
                  value={gbifSearchQuery}
                  onChange={(e) => setGbifSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      openGbifSearchDialog(e.target.value);
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => openGbifSearchDialog(gbifSearchQuery)}
                  disabled={gbifSearchLoading}
                >
                  再検索
                </Button>
              </Stack>

              {gbifSearchError && <Alert severity="error">{gbifSearchError}</Alert>}

              {gbifSearchLoading ? (
                <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" color="text.secondary">
                    GBIFを検索中...
                  </Typography>
                </Stack>
              ) : (
                <List
                  dense
                  sx={{
                    maxHeight: 360,
                    overflow: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  {gbifSearchResults.map((r, idx) => {
                    const key = r?.key ? String(r.key) : "";
                    const primary = r?.scientificName || r?.canonicalName || "-";
                    const authorship = r?.authorship || r?.authorshipString || r?.author || "";
                    const rank = (r?.rank || r?.rankString || "").toString();
                    const status = (r?.status || r?.taxonomicStatus || "").toString();

                    // Build a parent chain from available fields
                    const higher = r?.higherClassification || r?.higherClassification || [];
                    const higherArr = Array.isArray(higher) ? higher : typeof higher === "string" && higher ? higher.split("|") : [];
                    const family = r?.family || "";
                    const genus = r?.genus || "";
                    const subfamily = r?.subfamily || "";
                    const parentChain = [
                      ...higherArr,
                      subfamily,
                      family,
                      genus,
                    ]
                      .filter(Boolean)
                      .slice(-3) // show up to 3 higher levels
                      .join(" › ");

                    const statusLower = (status || "").toLowerCase();

                    return (
                      <React.Fragment key={key || `gbif-${idx}`}>
                        <ListItemButton
                          selected={selectedGbifKey === key}
                          disabled={!key}
                          onClick={() => setSelectedGbifKey(key)}
                        >
                          <Box sx={{ width: "100%" }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Typography variant="body1">{primary}</Typography>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {key && <Chip size="small" label={`GBIF: ${key}`} />}
                                {rank && <Chip size="small" label={rank} variant="outlined" />}
                                {statusLower === "accepted" ? (
                                  <Chip size="small" label="accepted" color="success" />
                                ) : status ? (
                                  <Chip size="small" label={status} color="warning" variant="outlined" />
                                ) : null}
                                {typeof r.matchConfidence === "number" ? (
                                  <Chip size="small" label={`conf: ${Math.round(r.matchConfidence * 100)}%`} variant="outlined" />
                                ) : null}
                                {typeof r.recommendedScore === "number" ? (
                                  <Chip size="small" label={`score: ${r.recommendedScore}`} color="primary" />
                                ) : null}
                              </Stack>
                            </Stack>

                            {authorship ? (
                              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace", mt: 0.5 }}>
                                {authorship}
                              </Typography>
                            ) : null}

                            {parentChain ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                {parentChain}
                              </Typography>
                            ) : null}

                            <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                              {r.datasetKey ? (
                                <Typography variant="caption" color="text.secondary">Source: {String(r.datasetKey)}</Typography>
                              ) : null}
                              {typeof r.occurrenceCount === "number" ? (
                                <Typography variant="caption" color="text.secondary">Occurrences: {r.occurrenceCount.toLocaleString()}</Typography>
                              ) : null}
                            </Stack>
                          </Box>
                        </ListItemButton>
                        <Divider component="li" />
                      </React.Fragment>
                    );
                  })}
                  {!gbifSearchResults.length && (
                    <Box component="li" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        検索結果がありません。
                      </Typography>
                    </Box>
                  )}
                </List>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGbifSearchOpen(false)}>閉じる</Button>
            <Button
              variant="outlined"
              onClick={() => handleApplyGbifSelection(false)}
              disabled={!selectedGbifKey || gbifSearchLoading}
            >
              選択を適用
            </Button>
            <Button
              variant="contained"
              onClick={() => handleApplyGbifSelection(true)}
              disabled={!selectedGbifKey || gbifSearchLoading || loading}
            >
              選択して配下を取得
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {conflictDialogOpen && (
        <Dialog open={conflictDialogOpen} onClose={() => setConflictDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>既存データとの不一致</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                同じID/GBIF IDの行で親またはランクが異なるデータが見つかりました。どちらを採用するか選択してください。
              </Typography>
              <Stack spacing={1}>
                {conflicts.map((c) => {
                  const key = String(c.incoming.id);
                  const sel = conflictSelections[key] || "new";
                  return (
                    <Paper key={key} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">ID: {c.incoming.id}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          GBIF ID: {c.incoming.gbif_id || "-"}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            既存
                          </Typography>
                          <Typography variant="body2">Parent: {c.existing.current_parent || "-"}</Typography>
                          <Typography variant="body2">Rank: {c.existing.current_rank || "-"}</Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            新規
                          </Typography>
                          <Typography variant="body2">Parent: {c.incoming.current_parent || "-"}</Typography>
                          <Typography variant="body2">Rank: {c.incoming.current_rank || "-"}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant={sel === "existing" ? "contained" : "outlined"}
                          onClick={() =>
                            setConflictSelections((prev) => ({
                              ...prev,
                              [key]: "existing",
                            }))
                          }
                        >
                          既存を採用
                        </Button>
                        <Button
                          size="small"
                          variant={sel === "new" ? "contained" : "outlined"}
                          onClick={() =>
                            setConflictSelections((prev) => ({
                              ...prev,
                              [key]: "new",
                            }))
                          }
                        >
                          新規を採用
                        </Button>
                        <Button
                          size="small"
                          variant={sel === "skip" ? "contained" : "outlined"}
                          onClick={() =>
                            setConflictSelections((prev) => ({
                              ...prev,
                              [key]: "skip",
                            }))
                          }
                        >
                          スキップ
                        </Button>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConflictDialogOpen(false)}>キャンセル</Button>
            <Button
              variant="contained"
              onClick={async () => {
                const resolvedPayload = [
                  ...(pendingPayload || []),
                  ...conflicts
                    .filter((c) => (conflictSelections[String(c.incoming.id)] || "new") === "new")
                    .map((c) => c.incoming),
                ];
                setConflictDialogOpen(false);
                await processPayload(resolvedPayload, pendingSelectedRows || []);
                setConflicts([]);
                setPendingPayload(null);
                setPendingSelectedRows(null);
              }}
            >
              適用して続行
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
